import { useEffect, useRef } from 'react'

// tripla's SDK appends its search bar to the end of <body> as #tripla-searching-app
// and pins it to the middle of the first viewport, on top of the hero image.
// We host the node ourselves instead: floating under the menu on desktop, and at
// the bottom of the hero screen on phones — see assets/css/tripla.css.
const SEARCH_APP_ID = 'tripla-searching-app'
const HERO_SELECTOR = 'section.slider-area'
const MOBILE_QUERY = '(max-width: 991px)'

function TriplaSearchBar() {
    const anchorRef = useRef(null)
    const appRef = useRef(null)
    const offsetRef = useRef(null)

    useEffect(() => {
        const anchor = anchorRef.current
        if (!anchor) return undefined

        const place = () => {
            const app = appRef.current || document.getElementById(SEARCH_APP_ID)
            if (!app) return
            appRef.current = app
            app.classList.add('lhc-tripla-hosted')

            // On a phone the hero fills the screen, so the widget drops to the
            // bottom of it; pages without a hero keep it up under the menu.
            const overHero = window.matchMedia(MOBILE_QUERY).matches
                && !!document.querySelector(HERO_SELECTOR)
            app.classList.toggle('lhc-tripla-over-hero', overHero)

            if (overHero) {
                // How far the anchor sits below the top of the page — the header
                // is a different height on each phone, so measure it rather than
                // hard-coding it, and let the CSS subtract it from the viewport.
                const offset = Math.round(anchor.getBoundingClientRect().top + window.pageYOffset)
                if (offset !== offsetRef.current) {
                    offsetRef.current = offset
                    anchor.style.setProperty('--lhc-anchor-offset', offset + 'px')
                }
            }
            if (app.parentElement !== anchor) {
                anchor.appendChild(app)
            }
        }

        let scheduled = false
        const schedule = () => {
            if (scheduled) return
            scheduled = true
            window.requestAnimationFrame(() => {
                scheduled = false
                place()
            })
        }

        // The SDK loads async and the hero arrives with the lazy-loaded page, so
        // watch the DOM settle instead of assuming either one is there yet.
        const observer = new MutationObserver(schedule)
        observer.observe(document.body, { childList: true, subtree: true })
        window.addEventListener('resize', schedule)
        place()

        return () => {
            observer.disconnect()
            window.removeEventListener('resize', schedule)
            // Hand the node back to <body> so React does not tear tripla's app
            // down along with whatever is hosting it.
            const app = appRef.current
            if (app && app.parentElement !== document.body) {
                app.classList.remove('lhc-tripla-hosted', 'lhc-tripla-over-hero')
                document.body.appendChild(app)
            }
        }
    }, [])

    return <div id="tripla-search-anchor" ref={anchorRef} />
}

export default TriplaSearchBar
